/**
 * Utilities for yielding control to the browser/event loop and running chunked work incrementally.
 */

/**
 * Yield to the main thread to allow paint and input events.
 * Uses scheduler.yield() when available (Chrome 115+), otherwise setTimeout(0).
 * Unlike yieldToBrowser() which uses requestIdleCallback, this function resumes
 * promptly and is suitable for time-budgeted yielding in hot loops.
 */
export function yieldToMain(): Promise<void> {
  if (
    typeof globalThis !== "undefined" &&
    "scheduler" in globalThis &&
    typeof (globalThis as any).scheduler?.yield === "function"
  ) {
    return (globalThis as any).scheduler.yield();
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Yield to the browser/UI thread to keep interactions responsive.
 * Uses requestIdleCallback when available, otherwise setTimeout(0).
 */
export function yieldToBrowser(): Promise<void> {
  if (typeof window !== "undefined" && (window as any).requestIdleCallback) {
    return new Promise((resolve) => (window as any).requestIdleCallback(() => resolve()));
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
}

interface RunChunkedOptions<T> {
  readonly items: readonly T[];
  readonly batchSize?: number;
  readonly processBatch: (batch: readonly T[], batchIndex: number) => void | Promise<void>;
  readonly yieldBetweenBatches?: boolean;
}

/**
 * Run work across a list of items in fixed-size batches, yielding between batches.
 */
export async function runChunked<T>({
  items,
  batchSize = 200,
  processBatch,
  yieldBetweenBatches = true,
}: RunChunkedOptions<T>): Promise<void> {
  if (batchSize <= 0) {
    throw new Error("batchSize must be positive");
  }

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    await processBatch(batch, Math.floor(i / batchSize));
    if (yieldBetweenBatches) {
      await yieldToBrowser();
    }
  }
}
